// Terms & Conditions acceptance API client.
//
// USE_REAL_API-gated like the other api/<x>.ts modules: real engine hits the
// live /agreements surface, maket/dev mode (MSW) the same relative paths
// intercepted by the dev simulation. fetch + apiUrl + throw on !res.ok.
//
// Error contract: the agreements HTTP surface returns `{ detail: { code,
// message } }` on 4xx (FastAPI HTTPException dicts); the central terms gate
// returns `{ error: { code, message } }` on its 403/503. readError() accepts
// both so the caller can branch on the typed `code` — notably
// `terms_verification_unavailable` (transient, retryable) and
// `idempotency_conflict` (409, re-fetch).

import { apiUrl } from '../lib/api'

export const TERMS_VERIFICATION_UNAVAILABLE = 'terms_verification_unavailable'
export const IDEMPOTENCY_CONFLICT = 'idempotency_conflict'

export type RequiredAgreementItem = {
  agreement_id: string
  agreement_version_id: string
  agreement_type: string
  title: string
  status: string
  can_current_user_accept: boolean
}

export type AgreementVersionOut = {
  id: string
  agreement_id: string
  version: number
  status: string
  effective_at?: string | null
  content: string
  content_sha256: string
  // Phase 5A: the localized rendering + the languages the signer can switch to.
  locale: string
  available_locales: string[]
}

export type LegalIdentityOut = {
  org_id: string
  org_type: string
  name: string
  suggested_legal_name: string
  country?: string | null
  state?: string | null
}

export type AcceptRequest = {
  authority_attested: boolean
  electronic_consent: boolean
  program_acknowledgement: boolean
  signer_full_name: string
  signer_title?: string | null
  org_legal_name: string
  org_country: string
  org_state?: string | null
  signature: string
  current_password: string
  idempotency_key: string
  // Phase 5A: the locale of the exact text the signer saw + accepted.
  accepted_locale?: string
}

export type AcceptResult = {
  acceptance_id: string
  packet_download_url?: string | null
  evidence_schema_version: number
  replayed: boolean
}

export type PacketDownloadOut = {
  acceptance_id: string
  download_url: string
  expires_in_seconds: number
}

export class AgreementsError extends Error {
  code: string
  status: number
  constructor(code: string, message: string, status: number) {
    super(message)
    this.name = 'AgreementsError'
    this.code = code
    this.status = status
  }
  get isUnavailable(): boolean {
    return this.code === TERMS_VERIFICATION_UNAVAILABLE || this.status === 503
  }
  get isIdempotencyConflict(): boolean {
    return this.code === IDEMPOTENCY_CONFLICT || this.status === 409
  }
}

async function toError(res: Response): Promise<AgreementsError> {
  const body = (await res.json().catch(() => null)) as {
    detail?: { code?: string; message?: string } | string
    error?: { code?: string; message?: string }
  } | null
  const envelope =
    body && typeof body.detail === 'object' && body.detail
      ? body.detail
      : body?.error
  const code = envelope?.code ?? (res.status === 503 ? TERMS_VERIFICATION_UNAVAILABLE : 'error')
  const message =
    envelope?.message ??
    (typeof body?.detail === 'string' ? body.detail : `HTTP ${res.status}`)
  return new AgreementsError(code, message, res.status)
}

export function generateIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return 'idem-' + Math.random().toString(36).slice(2) + Date.now().toString(36)
}

export async function fetchRequiredAgreements(): Promise<RequiredAgreementItem[]> {
  const res = await fetch(apiUrl('/agreements/required'))
  if (!res.ok) throw await toError(res)
  return (await res.json()) as RequiredAgreementItem[]
}

export async function fetchAgreementVersion(
  agreementId: string,
  locale?: string,
): Promise<AgreementVersionOut> {
  const suffix = locale ? `?locale=${encodeURIComponent(locale)}` : ''
  const res = await fetch(apiUrl(`/agreements/${agreementId}/versions/current${suffix}`))
  if (!res.ok) throw await toError(res)
  return (await res.json()) as AgreementVersionOut
}

export async function fetchLegalIdentity(): Promise<LegalIdentityOut> {
  const res = await fetch(apiUrl('/orgs/current/legal-identity'))
  if (!res.ok) throw await toError(res)
  return (await res.json()) as LegalIdentityOut
}

export async function acceptAgreement(
  agreementId: string,
  body: AcceptRequest,
): Promise<AcceptResult> {
  const res = await fetch(apiUrl(`/agreements/${agreementId}/accept`), {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  })
  if (!res.ok) throw await toError(res)
  return (await res.json()) as AcceptResult
}

export async function fetchAcceptancePacket(
  acceptanceId: string,
): Promise<PacketDownloadOut> {
  const res = await fetch(apiUrl(`/agreements/acceptances/${acceptanceId}/packet`))
  if (!res.ok) throw await toError(res)
  return (await res.json()) as PacketDownloadOut
}
